import { z } from 'zod';
import { OrgPluginInstallationStatusSchema } from './ws.types.js';
import { PluginReleaseChannelSchema, PluginReleaseStatusSchema } from './organization.types.js';

/**
 * Per-org plugin installation row enriched with the static plugin
 * metadata needed by list/detail UIs (so they don't have to join the
 * catalog response client-side). Mirrors the server's `InstallationView`
 * exactly — the server constructs this in
 * `PluginInstallationService.buildView`.
 */
export const InstallationViewSchema = z.object({
  organizationId: z.string(),
  pluginName: z.string(),
  status: OrgPluginInstallationStatusSchema,
  installedAt: z.number().nullable(),
  installedByUserId: z.string().nullable(),
  uninstalledAt: z.number().nullable(),
  lastTransitionAt: z.number(),
  lastError: z.string().nullable(),
  // Plugin metadata for display (sourced from PluginDefinition).
  version: z.string(),
  description: z.string().optional(),
  icon: z.string().optional(),
  tags: z.array(z.string()).optional(),
  /** Plugin names this plugin requires. */
  dependencies: z.array(z.string()),
  /**
   * Plugin names that depend on this plugin (full transitive closure
   * across the static plugin graph, restricted to installable plugins).
   */
  dependents: z.array(z.string()),
  /**
   * Org capability slugs that ALL must be present on the active org for
   * this plugin to be available. Omitted when the plugin has no
   * capability gate. Surfaces in the per-org detail UI so operators
   * understand why a plugin is unavailable / which slug unlocks it.
   */
  requiredCapabilities: z.array(z.string()).optional(),
  /**
   * Maturity tier this plugin currently ships at. Omitted for system
   * plugins. The per-org UI uses this to render a release-status pill
   * and to pair pre-release plugins with the required channel.
   */
  releaseStatus: PluginReleaseStatusSchema.optional(),
  /**
   * Pre-release channel the org must opt into before this plugin
   * appears as installable. Omitted when the plugin is `released`.
   */
  requiredReleaseChannel: PluginReleaseChannelSchema.optional(),
  /**
   * Populated only for stranded `installed` rows where the org has since
   * lost (or never had) one of the required gates — drives the
   * "Unavailable" badge without dropping the row (admins still need to
   * see the install to clean it up). `missing_capabilities` corresponds
   * to a revoked org capability; `channel_not_enabled` corresponds to a
   * pre-release plugin whose channel was turned off after install.
   */
  unavailableReason: z.enum(['missing_capabilities', 'channel_not_enabled']).optional(),
  /**
   * Capability slugs the org is missing, paired with `unavailableReason`.
   * Empty / omitted when the org satisfies every required slot.
   */
  missingCapabilities: z.array(z.string()).optional(),
});
export type InstallationView = z.infer<typeof InstallationViewSchema>;

/**
 * Org-agnostic catalog entry returned by `GET /plugins/catalog`. Used by
 * the org-creation dialog and setup wizard before an organization id
 * exists.
 */
export const PluginCatalogEntrySchema = z.object({
  name: z.string(),
  version: z.string(),
  description: z.string().optional(),
  icon: z.string().optional(),
  tags: z.array(z.string()).optional(),
  defaultInstalled: z.boolean().optional(),
  dependencies: z.array(z.string()),
  releaseStatus: PluginReleaseStatusSchema.optional(),
});
export type PluginCatalogEntry = z.infer<typeof PluginCatalogEntrySchema>;

export const PluginCatalogResponseSchema = z.object({
  installable: z.array(PluginCatalogEntrySchema),
  system: z.array(PluginCatalogEntrySchema),
});
export type PluginCatalogResponse = z.infer<typeof PluginCatalogResponseSchema>;

/**
 * Per-org plugin list response. Plugins are bucketed into:
 * - `installed` — terminal-state installed for this org.
 * - `available` — installable plugins not currently installed (any
 *   non-installed status; transient + uninstalled + missing rows).
 * - `system` — `installable: false` plugins, unconditionally active.
 */
export const OrgPluginListResponseSchema = z.object({
  installed: z.array(InstallationViewSchema),
  available: z.array(InstallationViewSchema),
  system: z.array(InstallationViewSchema),
});
export type OrgPluginListResponse = z.infer<typeof OrgPluginListResponseSchema>;

export const OrgPluginUninstallPreviewSchema = z.object({
  cascaded: z.array(z.string()),
});
export type OrgPluginUninstallPreview = z.infer<typeof OrgPluginUninstallPreviewSchema>;
